import { useToast } from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { padStart, uniq } from 'lodash';

import { getPersonName } from 'utils';
import { useAuthContext } from 'views/auth/Auth';

import {
    AssetClass,
    Configuration,
    GenerateFidelityBondTradesCsvRequest,
    GenerateFidelityEquityTradesCsvRequest,
    type GenerateSchwabAggregatedBondTradesRequest,
    type GenerateSchwabAggregatedEquityTradesRequest,
    type GenerateSchwabBondAllocationFileRequest,
    type GenerateSchwabEquityAllocationFileRequest,
    type GenerateSchwabEquityBlockTradesCsvRequest,
    type GenerateSchwabEquityTradesCsvRequest,
    type GetBlocksAndExecutionsRequest,
    type GetSchwabAggregatedBondTradesRequest,
    type NdvrTrade,
    type SaveSchwabExecutedBondTradesRequest,
    TradeExecutionApi,
} from '../_openapi/portfolio-optimizer';
import { ManualTrade } from '../TradeBlotter/TradeBlotter';
import { tradeCalculator } from '../TradeBlotter/tradeCalculator';
import { formatDate, formatDateTime, parseDate, SERVER_DATE_FORMAT, SERVER_DATE_TIME_FORMAT } from '../utils/dateUtils';
import { downloadFileFromResponse } from '../utils/downloadFile';
import { lookupAsset } from './asset';
import { defaultApiConfig } from './defaultApiConfig';
import { lookupPortfolio } from './portfolio';

const optimizationApi = new TradeExecutionApi(new Configuration(defaultApiConfig));

export const useSubmitManualTrades = () => {
    const toast = useToast();
    const { user } = useAuthContext();

    return useMutation({
        mutationFn: ({ reason, trades }: { reason: string; trades: ManualTrade[] }) => {
            const now = new Date();
            const timestamp = now.valueOf();

            const ndvrTrade = trades.map((trade, idx) => {
                return {
                    orderId: `${timestamp}_${padStart(String(idx), 4, '0')}`,
                    tradeDate: formatDate(now, SERVER_DATE_FORMAT),
                    pId: trade.portfolio?.pId,
                    portfolioGuid: trade.portfolio?.portfolioGuid,
                    side: trade.side,
                    quantity: trade.quantity,
                    aC: trade.portfolio?.aC,
                    brokerId: trade.portfolio?.brokerId,
                    orderType: trade.orderType,
                    quotePrice: tradeCalculator.getCleanPrice(trade),
                    limitPrice: tradeCalculator.getAdjustedCleanPrice(trade),
                    priceSource: trade.quote?.source,
                    priceTimestamp: formatDateTime(parseDate(trade.quote?.timestamp), SERVER_DATE_TIME_FORMAT),
                    isManualTrade: true,
                    submitter: getPersonName(user),
                    reason,
                    createdAt: formatDateTime(now, SERVER_DATE_TIME_FORMAT),
                    security: {
                        sId: trade.asset?.sId,
                        symbol: trade.asset?.symbol,
                        description: trade.asset?.description,
                        primaryExchange: trade.asset?.primaryExchange,
                        assetType: trade.asset?.assetType,
                        assetClass: trade.asset?.assetClass,
                        cusip: trade.asset?.cusip,
                        maturityDate: trade.asset?.maturityDate,
                        issuer: trade.asset?.issuer,
                        coupon: trade.asset?.coupon,
                        contractMultiplier: trade.asset?.contractMultiplier,
                        priceMultiplier: trade.asset?.priceMultiplier,
                        expirationDate: trade.asset?.expirationDate,
                        ndvrChainId: trade.asset?.ndvrChainId,
                        isin: trade.asset?.isin,
                        shareClassId: trade.asset?.scId,
                        companyId: trade.asset?.coId,
                    },
                };
            }) as NdvrTrade[];

            return optimizationApi.submitManualNdvrTrades({ ndvrTrade });
        },
        onSuccess: () => {
            toast({
                title: 'Trades submitted',
                status: 'success',
            });
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to submit trades',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateSchwabEquityTradesCsv = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: async (request: GenerateSchwabEquityTradesCsvRequest) => {
            const response = await optimizationApi.generateSchwabEquityTradesCsvRaw(request);
            await downloadFileFromResponse(response.raw);
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to download Schwab equity trades CSV',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateFidelityEquityTradesCsv = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: async (request: GenerateFidelityEquityTradesCsvRequest) => {
            const response = await optimizationApi.generateFidelityEquityTradesCsvRaw(request);
            await downloadFileFromResponse(response.raw);
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to download Fidelity equity trades CSV',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateFidelityBondTradesCsv = (optimizationId: number) => {
    const toast = useToast();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (request: GenerateFidelityBondTradesCsvRequest) => {
            const response = await optimizationApi.generateFidelityBondTradesCsvRaw(request);
            await downloadFileFromResponse(response.raw);
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to download Fidelity bond trades CSV',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['optimization', optimizationId, 'block-trades', AssetClass.BOND],
            });
        },
    });
};

export const useGetBlocksAndExecutions = (request: GetBlocksAndExecutionsRequest, enabled: boolean) =>
    useQuery({
        queryKey: ['optimization', request.optimizationId, 'block-trades', request.assetClass],
        queryFn: () => optimizationApi.getBlocksAndExecutions(request),
        enabled: enabled,
    });

export const useSchwabAggregatedBondTrades = (request: GetSchwabAggregatedBondTradesRequest) =>
    useQuery({
        queryKey: ['optimization', request.optimizationId, 'schwab-aggregated-bond-trades'],
        queryFn: () => optimizationApi.getSchwabAggregatedBondTrades(request),
    });

export const useGenerateSchwabAggregatedBondTrades = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (request: GenerateSchwabAggregatedBondTradesRequest) => {
            return optimizationApi.generateSchwabAggregatedBondTrades(request);
        },
        onSuccess: (_response, request) => {
            queryClient.invalidateQueries({
                queryKey: ['optimization', request.optimizationId, 'schwab-aggregated-bond-trades'],
            });
            toast({
                title: 'Generated Schwab aggregated bond trades',
                status: 'success',
            });
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to generate Schwab aggregated bond trades',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateSchwabBondAllocationFile = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: async (request: GenerateSchwabBondAllocationFileRequest) => {
            const response = await optimizationApi.generateSchwabBondAllocationFileRaw(request);
            await downloadFileFromResponse(response.raw);
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to download Schwab bond allocation file',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateSchwabAggregatedEquityTrades = () => {
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationFn: (request: GenerateSchwabAggregatedEquityTradesRequest) => {
            return optimizationApi.generateSchwabAggregatedEquityTrades(request);
        },
        onSuccess: (_response, request) => {
            queryClient.invalidateQueries({
                queryKey: ['optimization', request.optimizationId, 'block-trades', AssetClass.EQUITY],
            });
            toast({
                title: 'Generated Schwab equity block trades',
                status: 'success',
            });
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to generate Schwab equity block trades',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateSchwabEquityBlockTradesCsv = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: async (request: GenerateSchwabEquityBlockTradesCsvRequest) => {
            const response = await optimizationApi.generateSchwabEquityBlockTradesCsvRaw(request);
            await downloadFileFromResponse(response.raw);
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to download Schwab equity block trades CSV',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useGenerateSchwabEquityAllocationFile = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: async (request: GenerateSchwabEquityAllocationFileRequest) => {
            const response = await optimizationApi.generateSchwabEquityAllocationFileRaw(request);
            await downloadFileFromResponse(response.raw);
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to download Schwab equity allocation file',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const useSaveSchwabExecutedBondTrades = () => {
    const toast = useToast();

    return useMutation({
        mutationFn: (request: SaveSchwabExecutedBondTradesRequest) => {
            return optimizationApi.saveSchwabExecutedBondTrades(request);
        },
        onSuccess: () => {
            toast({
                title: 'Saved Schwab executed bond trades',
                status: 'success',
            });
        },
        onError: (error: Error) => {
            toast({
                title: 'Failed to save Schwab executed bond trades',
                description: error.message,
                status: 'error',
                duration: null,
            });
        },
    });
};

export const usePreloadResources = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (trades: ManualTrade[]) => {
            const portfolioIdentifiers = uniq(
                trades
                    .map((trade) => trade.portfolio?.portfolioGuid || trade.portfolio?.pId || trade.portfolio?.aC)
                    .filter(Boolean),
            );
            const portfolioRequests = portfolioIdentifiers.map(async (portfolioIdentifier) => {
                const portfolios = await lookupPortfolio(portfolioIdentifier!);
                queryClient.setQueryData(['portfolio', 'lookup', portfolioIdentifier], portfolios);
            });

            const assetIdentifiers = uniq(
                trades.map((trade) => trade.asset?.sId || trade.asset?.cusip || trade.asset?.symbol).filter(Boolean),
            );
            const securityRequests = assetIdentifiers.map(async (assetIdentifier) => {
                const assets = await lookupAsset(assetIdentifier!);
                queryClient.setQueryData(['asset', 'lookup', assetIdentifier], assets);
            });

            return Promise.all([...portfolioRequests, ...securityRequests]);
        },
    });
};
